import { NetworkStatus, useLazyQuery } from '@apollo/client';
import {
  bankersRound,
  ErrorBoundary,
  ErrorRetry,
  ImageViewer,
  PersonaMessages,
  useConfig,
  useDate,
  useImageViewerState,
  useName,
} from '@bechtel/client-common';
import { GET_USER_BLOCKS } from '@bechtel/client-gql';
import { Avatar, Box, Icon, IconSize, Spinner, SpinnerSize, Text, useSideSheetState } from '@bechtel/gds';
import {
  Attachment,
  Block,
  BlockFilterInput,
  BlockList,
  BlockStatusCode,
  BlockType,
  CommodityType,
  MilestoneType,
  PagingInput,
  Scalars,
} from '@bechtel/types';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { parseISO } from 'date-fns';
import { filter, get, map, maxBy, minBy, orderBy, sumBy } from 'lodash';
import { FC, useDeferredValue, useEffect, useState } from 'react';
import { BlockTypeMessages } from '~/components';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as NoBlocksIcon } from '@bechtel/client-assets/dist/images/no-blocks.svg';
import { ResolveBlock } from '~/resolve';
import { BlockDrawerValuesProps } from '../form-data';
import {
  BlockCardBorderBox,
  BlockCardDetailCommentStyled,
  BlockCardDetailStyled,
  BlockCardInfoStyled,
  BlockCardItemStyled,
  BlockCardLeftIconStyled,
  BlockCardlHeadingStyled,
  BlockCardRightIconStyled,
  BlockCardTopBorderStyled,
  BlockForemanSeparatorStyled,
  BlockImageSeparatorStyled,
  BlockStatusStyled,
  CommentTextStyled,
  FooterTextLabelStyled,
  FooterTextValueStyled,
  HeadingStyled,
  IconStyled,
  ImageBox,
  ImageBoxStyled,
  InfoStyled,
  ItemContainerStyled,
  NoBlocksFoundStyled,
  UserInfoStyled,
} from './styled';

interface ImageGalleryProps {
  attachments: Attachment[];
  isClosed: Scalars['Boolean'];
  openBlock: () => void;
}

interface BlockCardIconProps {
  icon: IconName;
  count: Scalars['Int'];
}
interface BlockListComponentProps {
  searchQuery: Scalars['String'];
  drawerValues?: BlockDrawerValuesProps;
}

const BlockCardInfo: FC<BlockCardIconProps> = ({ icon, count }) => {
  return (
    <BlockCardInfoStyled>
      <Icon iconStyle="solid" fillColor="red.800" size={IconSize.LARGE} icon={icon}></Icon>
      <Text size="200" color="grey.1000">
        {count && (count < 10 ? '0' + count : count)}
      </Text>
    </BlockCardInfoStyled>
  );
};
const ImageGallery: FC<ImageGalleryProps> = ({ attachments, isClosed, openBlock }) => {
  const imagesLength = attachments.length - 1;
  const [imagePosition, setImagePosition] = useState(0);
  const { open: openImageViewer, close: closeImageViewer, visible: visibleImageViewer } = useImageViewerState(false);
  const images = attachments.length > 0 ? map(attachments, (attachment) => attachment.url) : [];

  const setNextGalleryImage = () => {
    if (imagesLength <= imagePosition) {
      return;
    }
    setImagePosition(imagePosition + 1);
  };

  const setPrevGalleryImage = () => {
    if (imagePosition < 1) {
      return;
    }
    setImagePosition(imagePosition - 1);
  };

  return (
    <>
      {visibleImageViewer && (
        <ImageViewer
          images={images}
          selectedImageIndex={imagePosition}
          visible={visibleImageViewer}
          closable={true}
          onDismiss={closeImageViewer}
        />
      )}
      <ImageBoxStyled data-testid={`block-card-image`}>
        {imagesLength < 0 ? (
          <ImageBox isClosed={isClosed} onClick={openBlock}>
            <IconStyled isClosed={isClosed} />
            <Box mt={{ sm: '450' }}>
              <Text size="200" color="grey[600]" family="graphik">
                <Trans>No Image Uploaded</Trans>
              </Text>
            </Box>
          </ImageBox>
        ) : (
          <Box
            data-testid="block-card-image-viewer"
            onClick={(e) => {
              e.stopPropagation();
              openImageViewer();
            }}
            style={{ flexGrow: 1, position: 'relative' }}
          >
            <img src={attachments[imagePosition]['url']} style={{ height: '100%', width: '100%' }} />
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }}>
              {imagePosition > 0 && (
                <BlockCardLeftIconStyled
                  data-testid="block-card-left-chevron"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPrevGalleryImage();
                  }}
                >
                  <Icon size={IconSize.MEDIUM} icon="chevron-left" />
                </BlockCardLeftIconStyled>
              )}
              {imagePosition < imagesLength && (
                <BlockCardRightIconStyled
                  data-testid="block-card-right-chevron"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNextGalleryImage();
                  }}
                >
                  <Icon size={IconSize.MEDIUM} icon="chevron-right" />
                </BlockCardRightIconStyled>
              )}
            </Box>
          </Box>
        )}
      </ImageBoxStyled>
    </>
  );
};

interface BlockCardTitleProps {
  heading: string;
  text: number | string;
}
const BlockCardDetailInfo: FC<BlockCardTitleProps> = ({ heading, text }) => {
  return (
    <BlockCardDetailStyled>
      <Text color="grey.1000">{heading}:</Text>
      <Box pl={{ sm: '300' }}>
        <Text color="grey.600">{text}</Text>
      </Box>
    </BlockCardDetailStyled>
  );
};

export const BlockItem: FC<Block> = (item) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  const { formatISODate, formatRelativeInWords } = useDate();
  const { dateFormat, blockAgeDateFormat } = useConfig();
  const { visible, open, close } = useSideSheetState(false);
  const milestoneHours = sumBy(item.impactedMilestones, 'hours');
  const orderedComments = orderBy(item && item.comments, ['createdAt'], ['desc']);
  const lastUpdated = maxBy(orderedComments, 'createdAt')?.createdAt;
  const blockCreatedDate = minBy(orderedComments, 'createdAt')?.createdAt;
  const parsedBlockCreatedDate = parseISO(blockCreatedDate);
  const isClosed = item.currentStatusI18n === BlockStatusCode.BLOCK_RESOLVED;
  const commentslength = isClosed ? orderedComments.length - 1 : orderedComments.length;
  if (isClosed) orderedComments.shift();
  const assignedCrewMembers = [
    ...item.workPackage.primaryOwners,
    ...item.workPackage.secondaryOwners,
    ...item.workPackage.otherOwners,
  ];
  const blockAgeWords = formatRelativeInWords(parsedBlockCreatedDate);
  //TODO: Open call for ResolveBlock Sidsesheet is hooked into individual components as image full viewer
  // close is triggering OpenSidesheetModal. Need to identify stopPropogation issue with Modals.
  return (
    <BlockCardItemStyled isClosed={isClosed}>
      {visible && (
        <ResolveBlock
          facilities={item.workPackage.facilities}
          workPackageId={item.workPackage.id}
          workPackageTag={item.workPackage.tag}
          assignedCrewMembers={assignedCrewMembers}
          block={item}
          onClose={close}
        />
      )}
      <BlockCardTopBorderStyled isClosed={isClosed} onClick={open} />
      <BlockCardBorderBox isClosed={isClosed}>
        <BlockCardlHeadingStyled isClosed={isClosed} onClick={open}>
          <Box css={{ overflow: 'hidden' }}>
            <HeadingStyled data-testid={`block-card-heading`}>
              <Trans id={BlockTypeMessages[item.type.title].id} />
              <Box pt={{ sm: '400' }}>
                <Text size="250">{item.workPackage.tag}</Text>
              </Box>
            </HeadingStyled>
          </Box>
          {
            <BlockStatusStyled color={isClosed ? 'grey.1000' : 'red.700'}>
              {isClosed ? t`CLOSED` : `${blockAgeWords} (${formatISODate(blockCreatedDate, blockAgeDateFormat)})`}
            </BlockStatusStyled>
          }
        </BlockCardlHeadingStyled>
        <ImageGallery attachments={item.attachment} isClosed={isClosed} openBlock={open} />
        <InfoStyled data-testid="block-card-detail-info" onClick={open}>
          <BlockCardDetailCommentStyled>
            <Text color="grey.1000">
              <Trans>Comments:</Trans>
            </Text>
            <Box px={{ sm: '300' }}>
              <CommentTextStyled>
                {orderedComments && orderedComments.length > 0 ? orderedComments[0].comment : ''}
              </CommentTextStyled>
            </Box>
          </BlockCardDetailCommentStyled>
          <BlockCardDetailInfo
            heading={
              item.impactedMilestones && item.impactedMilestones.length > 1
                ? t`Milestones Impacted`
                : t`Milestone Impacted`
            }
            text={`${item.impactedMilestones.length} (${bankersRound(milestoneHours, 0)} ${t`Hrs`})`}
          />
          <BlockCardDetailInfo
            heading={
              item.impactedCommodities && item.impactedCommodities.length > 1
                ? t`Commodities Impacted`
                : t`Commodity Impacted`
            }
            text={item.impactedCommodities.length}
          />
        </InfoStyled>
        <BlockImageSeparatorStyled isClosed={isClosed} onClick={open} />
        <Box px={{ sm: '400' }} pb={{ sm: '400' }} onClick={open}>
          <UserInfoStyled>
            <Avatar src={item.creator && item.creator.imageUrl ? item.creator.imageUrl : undefined} />
            <Box pl={{ sm: '200' }} flexDirection={{ sm: 'column' }}>
              <FooterTextLabelStyled>
                {item.creator && getFullName(item.creator.firstName, item.creator.lastName)}
              </FooterTextLabelStyled>
              <FooterTextValueStyled>
                {item.creator.persona && i18n._(PersonaMessages[item.creator.persona])}
              </FooterTextValueStyled>
            </Box>
          </UserInfoStyled>
          <BlockForemanSeparatorStyled isClosed={isClosed}>
            <Box>
              <Box pr={{ sm: '200' }}>
                <FooterTextLabelStyled>
                  <Trans>Updated:</Trans>
                </FooterTextLabelStyled>
              </Box>
              <FooterTextValueStyled>{formatISODate(lastUpdated, dateFormat)}</FooterTextValueStyled>
            </Box>
            <Box>
              {item.attachment.length > 0 && <BlockCardInfo icon="image" count={item.attachment.length} />}
              {commentslength > 0 && <BlockCardInfo icon="comment-alt" count={commentslength} />}
            </Box>
          </BlockForemanSeparatorStyled>
        </Box>
      </BlockCardBorderBox>
    </BlockCardItemStyled>
  );
};

const BlocksItemList = ({ items }: { items: Block[] }) => {
  const itemCount = get(items, 'length', 0);
  return (
    <ItemContainerStyled itemsInSingleRow={itemCount < 3}>
      {/* If less then 3 items align left*/}
      {items.map((item: Block, index: number) => {
        return (
          <Box key={`${index}.${item.id}`} data-testid={`blockcard-${item.id}`}>
            <ErrorBoundary>
              <BlockItem {...item} />
            </ErrorBoundary>
          </Box>
        );
      })}
    </ItemContainerStyled>
  );
};

export const BlockListComponent: FC<BlockListComponentProps> = ({ searchQuery, drawerValues }) => {
  const debounceSearchQuery = useDeferredValue(searchQuery?.trim());
  const blocksLimit = 10;
  const [loadBlocks, { called, loading, error, data, refetch, networkStatus, fetchMore }] = useLazyQuery<
    { blocks: BlockList },
    {
      paging: PagingInput;
      input: BlockFilterInput;
    }
  >(GET_USER_BLOCKS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (debounceSearchQuery?.length > 0 && debounceSearchQuery?.length < 3) return;
    loadBlocks({
      variables: {
        paging: {
          cursor: '0',
          limit: blocksLimit,
        },
        input: {
          searchKeyword: debounceSearchQuery,
          sortBy: drawerValues?.sortBy,
          orderBy: drawerValues?.orderBy,
          searchBy: drawerValues?.searchBy.value,
          assignedToMe: drawerValues?.searchScope.id === 'WP_ASSIGNED',
          filterByBlockStatuses: map(drawerValues?.blockStatus, (item) => item.codeI18n),
          filterByBlockTypes: map(drawerValues?.blockTypes, (item: BlockType) => item.id),
          filterByCommodityTypes: map(drawerValues?.commodityTypes, (item: CommodityType) => item.id),
          filterByMilestoneTypes: map(drawerValues?.milestoneList, (item: MilestoneType) => item.id),
        },
      },
    });
  }, [debounceSearchQuery, drawerValues, loadBlocks]);

  const blockDataItems: BlockList['items'] = get(data, 'blocks.items', []);
  const totalPages = Math.ceil(get(data, 'blocks.total', 1) / blocksLimit);
  const blockListCursor = get(data, 'blocks.cursor', 0);
  // If no block status filters or all the status are selected return api data or filter by status
  // Fix Me: This is to circumvent cache issue
  const selectedBlockStatusFilters = map(drawerValues?.blockStatus, (item) => item.codeI18n);
  const blockItems =
    !selectedBlockStatusFilters || selectedBlockStatusFilters.length == 0 || selectedBlockStatusFilters.length == 2
      ? blockDataItems
      : filter(blockDataItems, (item: Block) => item.currentStatusI18n == selectedBlockStatusFilters[0]);

  const handlScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      blockListCursor <= totalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: blockListCursor.toString(),
            limit: blocksLimit,
          },
        },
      });
    }
  };

  return (
    <Box
      onScroll={(e) => {
        handlScroll(e);
      }}
      py={{ sm: '550' }}
      px={{ sm: '600' }}
      flexDirection={{ sm: 'column' }}
      style={{ height: '100%', overflowY: 'auto' }}
    >
      {blockItems.length > 0 && <BlocksItemList items={blockItems} />}
      {(!called || loading || networkStatus === NetworkStatus.refetch) && (
        <Box
          flexGrow={{ sm: 1 }}
          alignItems={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          justifyContent={{ sm: 'center' }}
          data-testid="loading"
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <div data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Blocks. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </div>
      )}
      {!loading && !error && called && blockItems.length === 0 && (
        <NoBlocksFoundStyled data-testid="no-blocks-found">
          <Text size="500" weight="600" color="grey.600" family="graphik">
            <Trans>No blocks (that’s good news!)</Trans>
          </Text>
          <Box mt={{ sm: '700' }} mb={{ sm: '800' }} mx={{ sm: '500' }}>
            <Text
              size="400"
              family="graphik"
              color="grey.600"
              css={`
                text-align: center;
                line-height: 1.57;
              `}
            >
              <Trans>
                If you have an issue to report, navigate to the Work Packages module and create block against
                commodity(s) Milestone(s).
              </Trans>
            </Text>
          </Box>
          <NoBlocksIcon style={{ width: '210px', height: '149px' }} />
        </NoBlocksFoundStyled>
      )}
    </Box>
  );
};

export default BlockListComponent;
